export async function generateAESKey() {
  // generate a random AES key with 256-bit length
  return await crypto.subtle.generateKey(
    {
      name: 'AES-GCM', // AES-GCM is a common mode for AES
      length: 256, // key length in bits
    },
    true, // whether the key is extractable
    ['encrypt', 'decrypt'] // usages for the key
  );
}

// function to import a RSA public key from its PEM representation
async function importRSAPublicKey(pem: string) {
  // remove the PEM header, footer, and newlines
  const pemHeader = '-----BEGIN PUBLIC KEY-----';
  const pemFooter = '-----END PUBLIC KEY-----';
  const pemContents = pem
    .replace(pemHeader, '')
    .replace(pemFooter, '')
    .replace(/\s/g, '');

  // decode the Base64-encoded PEM key
  const binaryDer = Uint8Array.from(atob(pemContents), (char) =>
    char.charCodeAt(0)
  );

  // import the RSA public key
  return await crypto.subtle.importKey(
    'spki', // Format for public keys
    binaryDer.buffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-1', // hash algorithm to use with OAEP
    },
    true, // whether the key is extractable
    ['encrypt'] // key usage
  );
}

// function to encrypt text with RSA Public Key
async function encryptWithRSAPublicKey(
  publicKey: CryptoKey,
  plaintext: string
) {
  // encode the plaintext into a Uint8Array
  const encoder = new TextEncoder();
  const encodedText = encoder.encode(plaintext);

  // encrypt the plaintext
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    publicKey, // RSA public key
    encodedText // data to encrypt
  );

  // return the ciphertext as base64
  return btoa(String.fromCharCode(...new Uint8Array(ciphertext)));
}

// function to decrypt text using a symmetric key
export async function decryptText(
  key: CryptoKey,
  ivBase64: string,
  ciphertextBase64: string
) {
  // decode the Base64 IV and ciphertext back into Uint8Array
  const iv = Uint8Array.from(atob(ivBase64), (char) => char.charCodeAt(0));
  const ciphertext = Uint8Array.from(atob(ciphertextBase64), (char) =>
    char.charCodeAt(0)
  );

  // decrypt the ciphertext
  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv, // initialization vector
    },
    key, // AES key
    ciphertext // data to decrypt
  );

  // decode the decrypted buffer into a string
  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}

function hexToString(hex: string) {
  const uint8Array = new Uint8Array(
    (hex.match(/.{2}/g) ?? []).map((byte: string) => parseInt(byte, 16))
  );
  return new TextDecoder().decode(uint8Array);
}

// function to create a session key
export async function createSessionKey(key: CryptoKey, publicKeyHex: string) {
  // export the key as raw bytes
  const rawKey = await crypto.subtle.exportKey('raw', key);
  const publicKeyPem = hexToString(publicKeyHex);

  // encode the raw key in Base64
  const base64Key = btoa(String.fromCharCode(...new Uint8Array(rawKey)));

  // import the RSA public key
  const publicKey = await importRSAPublicKey(publicKeyPem);

  // encrypt the simmetric key with the public key
  const sessionKey = await encryptWithRSAPublicKey(publicKey, base64Key);
  return sessionKey;
}
